import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BundleScanFindingTriageHistoryEntry } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

export function TriageHistorySection({ signature, index }: { signature: string; index: number }) {
  const [open, setOpen] = useState(false);
  const historyQuery = useQuery<BundleScanFindingTriageHistoryEntry[]>({
    queryKey: ["/api/admin/secret-scan/triages", signature, "history"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/secret-scan/triages/${encodeURIComponent(signature)}/history`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load triage history");
      return res.json();
    },
    enabled: open,
  });
  return (
    <div className="pt-2 border-t" data-testid={`section-finding-triage-history-${index}`}>
      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        onClick={() => setOpen((o) => !o)}
        data-testid={`button-toggle-triage-history-${index}`}
      >
        {open ? "Hide" : "Show"} triage history
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`list-finding-triage-history-${index}`}>
          {historyQuery.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : historyQuery.isError ? (
            <p className="text-xs text-red-700" data-testid={`text-finding-triage-history-error-${index}`}>
              Failed to load triage history.
            </p>
          ) : !historyQuery.data || historyQuery.data.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid={`text-finding-triage-history-empty-${index}`}>
              No previous triage entries.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {historyQuery.data.map((entry) => {
                const statusColor =
                  entry.status === "acknowledged" ? "bg-blue-100 text-blue-800 border-blue-300" :
                  entry.status === "false_positive" ? "bg-gray-100 text-gray-700 border-gray-300" :
                  "bg-red-100 text-red-800 border-red-300";
                return (
                  <li key={entry.id} className="text-xs border-l-2 border-muted pl-2" data-testid={`row-finding-triage-history-${index}-${entry.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={statusColor}>
                        {entry.status === "false_positive" ? "false positive" : entry.status}
                      </Badge>
                      <span className="text-muted-foreground">
                        {new Date(entry.changedAt).toLocaleString()}
                      </span>
                      {entry.changedBy && (
                        <span className="text-muted-foreground">by {entry.changedBy}</span>
                      )}
                    </div>
                    {entry.note && (
                      <div className="mt-0.5 text-muted-foreground whitespace-pre-wrap break-words">
                        {entry.note}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
